import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConfirmDialog } from '../components/confirm-dialog.js';
import { Select } from '../components/select.js';
import { magToast } from '../components/toaster.js';
import { WorkspaceNav } from '../components/workspace-nav.js';
import { type Brand, fetchBrands } from '../lib/api.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { formatDate } from '../lib/format.js';
import {
  type CreateInvitationInput,
  createInvitation,
  InvitationApiError,
  type InvitationResponse,
  listInvitations,
  resendInvitation,
  revokeInvitation,
} from '../lib/invitations-api.js';
import {
  addBrandMember,
  changeBrandMemberRole,
  changeOrgMemberRole,
  fetchOrgMembers,
  type OrgMemberSummary,
  removeBrandMember,
  removeOrgMember,
  type WorkspaceRole,
} from '../lib/members-api.js';
import { meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// the team access design doc (CommitMind) T3 — workspace-level team page.
//
// Three sections:
//   - Active members (table with role chips + per-row actions for
//     owner/admin viewers).
//   - Pending invites (table with resend + revoke).
//   - Invite teammate (modal with email + role + per-brand grants).
//
// Org-scoped — per-brand team management lives at
// /brand/$slug/settings/team (out of scope for the first pass; the
// brand-access summary on each row of the org table gives a read-only
// view of who can see what for now).

const WORKSPACE_ROLE_OPTIONS = [
  { value: 'admin', label: 'admin', hint: 'every brand, every approval' },
  { value: 'member', label: 'member', hint: 'only the brands you grant' },
] as const;

const BRAND_GRANT_OPTIONS = [
  { value: '', label: 'no access' },
  { value: 'viewer', label: 'viewer', hint: 'read-only — sees the feed' },
  { value: 'editor', label: 'editor', hint: 'approves proposals' },
  { value: 'manager', label: 'manager', hint: 'full control of the brand' },
  // [designer-role] Specialized scope — not a step on the manager/
  // editor/viewer ladder. Designers see the brand at viewer access
  // but are surfaced to the channel "route to designer" picker on
  // the channels page.
  { value: 'designer', label: 'designer', hint: 'uploads assets, no approvals' },
] as const;

const teamMembersQuery = queryOptions({
  queryKey: ['team', 'members'] as const,
  queryFn: fetchOrgMembers,
});

const teamInvitationsQuery = queryOptions({
  queryKey: ['team', 'invitations'] as const,
  queryFn: listInvitations,
});

const brandsForInviteQuery = queryOptions({
  queryKey: ['team', 'brands-for-invite'] as const,
  queryFn: fetchBrands,
});

export const Route = createFileRoute('/settings/team')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    // Workspace members don't see the team list — they navigate by
    // brand only. Bounce to /brands so the URL never resolves with
    // the page rendered for them.
    if (me.workspaceRole !== 'owner' && me.workspaceRole !== 'admin') {
      throw redirect({ to: '/brands' });
    }
    await Promise.all([
      queryClient.ensureQueryData(teamMembersQuery).catch(() => []),
      queryClient.ensureQueryData(teamInvitationsQuery).catch(() => []),
      queryClient.ensureQueryData(brandsForInviteQuery).catch(() => []),
    ]);
    return { me };
  },
  // beforeLoad's return populates route context — useLoaderData reads
  // the loader's return value, so thread context through explicitly.
  loader: ({ context }) => context as { me: { id: string; email: string } },
  component: TeamSettingsPage,
});

function TeamSettingsPage() {
  const { t } = useLingui();

  const { me } = Route.useLoaderData() as { me: { id: string; email: string } };
  const queryClient = useQueryClient();
  const router = useRouter();

  const membersQ = useQuery(teamMembersQuery);
  const invitesQ = useQuery(teamInvitationsQuery);
  const brandsQ = useQuery(brandsForInviteQuery);

  const myMembership = membersQ.data?.find((m) => m.userId === me.id);
  const canManage =
    myMembership?.workspaceRole === 'owner' || myMembership?.workspaceRole === 'admin';

  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ userId: string; name: string } | null>(null);
  const [removing, setRemoving] = useState(false);

  const refresh = async () => {
    await refreshQueries(queryClient, ['team', 'members'], ['team', 'invitations']);
  };

  const handleRemove = async (userId: string) => {
    setRemoving(true);
    try {
      await removeOrgMember(userId);
      await refresh();
      magToast.success('done.', 'they no longer have access.');
      setConfirmRemove(null);
    } catch (err) {
      const message = founderErrorCopy(err, 'try again in a sec.');
      magToast.error("couldn't remove.", message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <WorkspaceNav active="team" />
      <section className="mx-auto max-w-[920px] px-7 pb-16 pt-8">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>team</Trans>
            </p>
            <h1 className="mt-2 font-display text-[34px] font-black leading-[1.05] tracking-[-0.04em]">
              <Trans>who's on the workspace.</Trans>
            </h1>
            <p className="mt-3 max-w-[640px] text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
              <Trans>admins manage everyone. members see only the brands they're granted.</Trans>
            </p>
          </div>
          {canManage ? (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="btn btn-primary self-end"
            >
              <Trans>invite teammate</Trans>
              <span className="btn-arrow">→</span>
            </button>
          ) : null}
        </header>

        <MembersSection
          members={membersQ.data ?? []}
          meId={me.id}
          canManage={canManage}
          loading={membersQ.isLoading}
          error={membersQ.error}
          brands={brandsQ.data ?? []}
          onBrandAccessChanged={refresh}
          onRequestRemove={(m) => setConfirmRemove({ userId: m.userId, name: m.name })}
          onChangeRole={async (m, role) => {
            try {
              await changeOrgMemberRole(m.userId, role);
              await refresh();
              magToast.success(
                'done.',
                `${m.name.toLowerCase()} is now a${role === 'admin' ? 'n admin' : ' member'}.`,
              );
            } catch (err) {
              magToast.error("couldn't change.", founderErrorCopy(err));
            }
          }}
        />

        <InvitesSection
          invites={invitesQ.data ?? []}
          canManage={canManage}
          loading={invitesQ.isLoading}
          onResend={async (inv) => {
            try {
              await resendInvitation(inv.id);
              await refresh();
              magToast.success('sent again.', `${inv.email} — fresh link, 7 days.`);
            } catch (err) {
              if (err instanceof InvitationApiError && err.status === 402) {
                magToast.error('seat cap hit.', err.message);
              } else {
                magToast.error("couldn't resend.", founderErrorCopy(err));
              }
            }
          }}
          onRevoke={async (inv) => {
            try {
              await revokeInvitation(inv.id);
              await refresh();
              magToast.success('pulled it back.', `${inv.email} can't use that link anymore.`);
            } catch (err) {
              magToast.error("couldn't revoke.", founderErrorCopy(err));
            }
          }}
        />

        {confirmRemove ? (
          <ConfirmDialog
            open
            title={`remove ${confirmRemove.name.toLowerCase()}?`}
            body={t`they'll lose access to every brand in this workspace. you can re-invite them later — they'll need to accept a fresh link.`}
            confirmLabel={t`remove`}
            destructive
            busy={removing}
            onCancel={() => setConfirmRemove(null)}
            onConfirm={() => handleRemove(confirmRemove.userId)}
          />
        ) : null}

        <InviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          brands={brandsQ.data ?? []}
          onSent={async () => {
            await refresh();
            await router.invalidate();
          }}
        />
      </section>
    </main>
  );
}

// ──────────────────────────────────────────────────────────────────

function workspaceRoleLabel(role: WorkspaceRole): string {
  return role; // already lowercase tokens
}

function brandAccessSummary(member: OrgMemberSummary): string {
  if (member.workspaceRole === 'owner' || member.workspaceRole === 'admin') {
    return 'every brand';
  }
  if (member.brandAccess.length === 0) return 'no brand access';
  if (member.brandAccess.length === 1) {
    const b = member.brandAccess[0];
    if (b) return `${b.role} on ${b.brandName.toLowerCase()}`;
  }
  return `${member.brandAccess.length} brands`;
}

function MembersSection({
  members,
  meId,
  canManage,
  loading,
  error,
  brands,
  onChangeRole,
  onRequestRemove,
  onBrandAccessChanged,
}: {
  members: OrgMemberSummary[];
  meId: string;
  canManage: boolean;
  loading: boolean;
  error: unknown;
  brands: Brand[];
  onChangeRole: (m: OrgMemberSummary, role: 'admin' | 'member') => Promise<void>;
  onRequestRemove: (m: OrgMemberSummary) => void;
  onBrandAccessChanged: () => Promise<void>;
}) {
  return (
    <article className="mt-7 rounded-2xl border border-[var(--line)] bg-white p-5">
      <header className="flex items-center justify-between">
        <h2 className="font-display text-[18px] font-bold tracking-[-0.01em]">
          <Trans>active members</Trans>
        </h2>
        <span className="font-mono text-[11px] text-[var(--ink)]/55">
          {members.length} <Plural value={members.length} one="seat" other="seats" />
        </span>
      </header>
      {loading ? (
        <p className="mt-3 text-[13px] text-[var(--ink)]/55">
          <Trans>reading…</Trans>
        </p>
      ) : error ? (
        <p className="mt-3 text-[13px] text-[var(--pulse)]">
          <Trans>couldn't load. try refreshing in a sec.</Trans>
        </p>
      ) : members.length === 0 ? (
        <p className="mt-3 text-[13px] text-[var(--ink)]/55">
          <Trans>no one's here yet.</Trans>
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--line)]">
          {members.map((m) => (
            <MemberRow
              key={m.userId}
              member={m}
              isMe={m.userId === meId}
              canManage={canManage}
              brands={brands}
              onChangeRole={onChangeRole}
              onRequestRemove={onRequestRemove}
              onBrandAccessChanged={onBrandAccessChanged}
            />
          ))}
        </ul>
      )}
    </article>
  );
}

function MemberRow({
  member,
  isMe,
  canManage,
  brands,
  onChangeRole,
  onRequestRemove,
  onBrandAccessChanged,
}: {
  member: OrgMemberSummary;
  isMe: boolean;
  canManage: boolean;
  brands: Brand[];
  onChangeRole: (m: OrgMemberSummary, role: 'admin' | 'member') => Promise<void>;
  onRequestRemove: (m: OrgMemberSummary) => void;
  onBrandAccessChanged: () => Promise<void>;
}) {
  const isOwner = member.workspaceRole === 'owner';
  const isMember = member.workspaceRole === 'member';
  // The owner's own row + your own row are read-only — you can't
  // demote yourself or the owner from here. Everyone else is mutable
  // for owners/admins.
  const editable = canManage && !isMe && !isOwner;
  // Brand-grant editor only applies when the row is a workspace
  // 'member' — admins and owners are implicit manager on every brand
  // (no per-brand row to edit). And only owners/admins can open it.
  const showBrandEditor = canManage && isMember && brands.length > 0;
  const [brandPanelOpen, setBrandPanelOpen] = useState(false);
  // Workspace-role chip is the resting state — keeps the row scannable.
  // "edit role" flips it into a Select. The chip and the dropdown
  // never coexist (no duplicate-control confusion).
  const [editingRole, setEditingRole] = useState(false);

  const initial = (member.name?.trim() || member.email?.trim() || '✦').charAt(0).toUpperCase();

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full border border-[var(--line)] bg-[var(--plum)] text-[var(--cream)]">
          {member.image ? (
            <img
              src={member.image}
              alt=""
              referrerPolicy="no-referrer"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="font-mono text-[11px] font-bold">{initial}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-[14px] font-semibold">
            {member.name.toLowerCase()}
            {isMe ? (
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/50">
                <Trans>(you)</Trans>
              </span>
            ) : null}
          </p>
          <p className="truncate font-mono text-[12px] text-[var(--ink)]/60">{member.email}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Resting state: static chip — scannable, no nested
              controls. Click "edit role" to flip into a Select; the
              row never shows both chip + select at the same time. */}
          {editable && editingRole ? (
            <>
              <Select<'admin' | 'member'>
                value={member.workspaceRole === 'admin' ? 'admin' : 'member'}
                onChange={async (role) => {
                  await onChangeRole(member, role);
                  setEditingRole(false);
                }}
                options={WORKSPACE_ROLE_OPTIONS}
                ariaLabel={`change workspace role for ${member.name}`}
                className="w-[148px]"
              />
              <button
                type="button"
                onClick={() => setEditingRole(false)}
                className="link-quiet text-[12px]"
              >
                <Trans>cancel</Trans>
              </button>
            </>
          ) : (
            <>
              <span className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/70">
                {workspaceRoleLabel(member.workspaceRole)}
              </span>
              {editable ? (
                <button
                  type="button"
                  onClick={() => setEditingRole(true)}
                  className="link-quiet text-[12px]"
                >
                  <Trans>edit role</Trans>
                </button>
              ) : null}
              {editable ? (
                <button
                  type="button"
                  onClick={() => onRequestRemove(member)}
                  className="link-quiet text-[12px]"
                >
                  <Trans>remove</Trans>
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Second row: brand-access summary + edit-access affordance. */}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 pl-12">
        <BrandAccessSummary member={member} />
        {showBrandEditor ? (
          <button
            type="button"
            onClick={() => setBrandPanelOpen((v) => !v)}
            aria-expanded={brandPanelOpen}
            className="link-quiet text-[12px]"
          >
            {brandPanelOpen ? <Trans>close</Trans> : <Trans>edit access</Trans>} →
          </button>
        ) : null}
      </div>

      {showBrandEditor && brandPanelOpen ? (
        <BrandAccessEditor member={member} brands={brands} onChanged={onBrandAccessChanged} />
      ) : null}
    </li>
  );
}

function BrandAccessSummary({ member }: { member: OrgMemberSummary }) {
  if (member.workspaceRole === 'owner' || member.workspaceRole === 'admin') {
    return (
      <p className="text-[12px] text-[var(--ink)]/55">
        <Trans>every brand (implicit · admin role)</Trans>
      </p>
    );
  }
  if (member.brandAccess.length === 0) {
    return (
      <p className="text-[12px] text-[var(--ink)]/55">
        <Trans>no brand access yet.</Trans>
      </p>
    );
  }
  return (
    <ul className="flex flex-wrap gap-1.5">
      {member.brandAccess.map((b) => (
        <li
          key={b.brandId}
          className="flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--paper)] px-2 py-0.5 text-[11px]"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/60">
            {b.role}
          </span>
          <span className="text-[var(--ink)]/75">{b.brandName.toLowerCase()}</span>
        </li>
      ))}
    </ul>
  );
}

function BrandAccessEditor({
  member,
  brands,
  onChanged,
}: {
  member: OrgMemberSummary;
  brands: Brand[];
  onChanged: () => Promise<void>;
}) {
  // brandId → in-flight flag, so per-row spinners don't block the
  // others. The member's brandAccess is the source-of-truth for what
  // role each brand currently carries; this state just tracks "is
  // this row mid-update?".
  const [busyByBrandId, setBusyByBrandId] = useState<Record<string, boolean>>({});
  const liveBrands = brands.filter((b) => b.archivedAt === null);
  const accessByBrand = new Map(member.brandAccess.map((a) => [a.brandId, a.role]));

  const handleChange = async (
    brandId: string,
    newRole: '' | 'manager' | 'editor' | 'viewer' | 'designer',
  ) => {
    setBusyByBrandId((m) => ({ ...m, [brandId]: true }));
    try {
      const current = accessByBrand.get(brandId);
      if (!newRole) {
        // '' = no access. Remove the brand_members row if one exists;
        // no-op otherwise.
        if (current) await removeBrandMember(brandId, member.userId);
      } else if (!current) {
        // Granting fresh access on this brand — POST a new row.
        await addBrandMember(brandId, member.userId, newRole);
      } else if (current !== newRole) {
        // Existing access, role change — PATCH.
        await changeBrandMemberRole(brandId, member.userId, newRole);
      }
      await onChanged();
      magToast.success('saved.', "i'll apply that next time they load.");
    } catch (err) {
      magToast.error("couldn't save.", founderErrorCopy(err, 'try again.'));
    } finally {
      setBusyByBrandId((m) => {
        const copy = { ...m };
        delete copy[brandId];
        return copy;
      });
    }
  };

  return (
    <div className="mt-3 ml-12 rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
        <Trans>brand access</Trans>
      </p>
      {liveBrands.length === 0 ? (
        <p className="mt-2 text-[12px] text-[var(--ink)]/55">
          <Trans>no brands in the workspace yet.</Trans>
        </p>
      ) : (
        <ul className="mt-2 flex flex-col divide-y divide-[var(--line)]">
          {liveBrands.map((b) => {
            const role = accessByBrand.get(b.id) ?? '';
            const busy = busyByBrandId[b.id] ?? false;
            return (
              <li key={b.id} className="flex items-center justify-between gap-3 py-2">
                <span className="truncate text-[13px]">{b.name.toLowerCase()}</span>
                <div className="w-[164px] shrink-0">
                  <Select<'' | 'manager' | 'editor' | 'viewer' | 'designer'>
                    value={role}
                    onChange={(next) => void handleChange(b.id, next)}
                    options={BRAND_GRANT_OPTIONS}
                    ariaLabel={`brand role for ${b.name}`}
                    disabled={busy}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function InvitesSection({
  invites,
  canManage,
  loading,
  onResend,
  onRevoke,
}: {
  invites: InvitationResponse[];
  canManage: boolean;
  loading: boolean;
  onResend: (inv: InvitationResponse) => Promise<void>;
  onRevoke: (inv: InvitationResponse) => Promise<void>;
}) {
  if (!canManage && invites.length === 0) return null;
  return (
    <article className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-5">
      <header className="flex items-center justify-between">
        <h2 className="font-display text-[18px] font-bold tracking-[-0.01em]">
          <Trans>pending invites</Trans>
        </h2>
        <span className="font-mono text-[11px] text-[var(--ink)]/55">
          {invites.length} <Plural value={invites.length} one="invite" other="invites" />
        </span>
      </header>
      {loading ? (
        <p className="mt-3 text-[13px] text-[var(--ink)]/55">
          <Trans>reading…</Trans>
        </p>
      ) : invites.length === 0 ? (
        <p className="mt-3 text-[13px] text-[var(--ink)]/55">
          <Trans>no pending invites.</Trans>
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--line)]">
          {invites.map((inv) => (
            <li key={inv.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-[13px]">{inv.email}</p>
                <p className="mt-0.5 text-[12px] text-[var(--ink)]/55">
                  {inv.workspaceRole === 'admin' ? (
                    <Trans>admin — every brand</Trans>
                  ) : (
                    <Plural
                      value={inv.brandGrants.length}
                      one="# brand grant"
                      other="# brand grants"
                    />
                  )}
                  <Trans> · expires {formatDate(inv.expiresAt)}</Trans>
                </p>
              </div>
              {canManage ? (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => void onResend(inv)}
                    className="link-quiet text-[12px]"
                  >
                    <Trans>resend</Trans>
                  </button>
                  <button
                    type="button"
                    onClick={() => void onRevoke(inv)}
                    className="link-quiet text-[12px] text-[var(--pulse)] hover:opacity-80"
                  >
                    <Trans>revoke</Trans>
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// ─── Invite modal ──────────────────────────────────────────────────

interface InviteModalProps {
  open: boolean;
  onClose: () => void;
  brands: Array<{ id: string; name: string; slug: string }>;
  onSent: () => void | Promise<void>;
}

function InviteModal({ open, onClose, brands, onSent }: InviteModalProps) {
  const { t } = useLingui();
  const [email, setEmail] = useState('');
  const [workspaceRole, setWorkspaceRole] = useState<'admin' | 'member'>('member');
  const [grants, setGrants] = useState<
    Record<string, 'manager' | 'editor' | 'viewer' | 'designer' | ''>
  >({});
  const [busy, setBusy] = useState(false);

  // Reset state on open so the next open is clean.
  useEffect(() => {
    if (!open) return;
    setEmail('');
    setWorkspaceRole('member');
    setGrants({});
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const input: CreateInvitationInput = {
        email: email.trim(),
        workspaceRole,
      };
      if (workspaceRole === 'member') {
        const brandGrants = Object.entries(grants)
          .filter((entry): entry is [string, 'manager' | 'editor' | 'viewer'] => entry[1] !== '')
          .map(([brandId, role]) => ({ brandId, role }));
        if (brandGrants.length === 0) {
          magToast.warning(
            'pick at least one brand.',
            'members need at least one grant — or invite as admin.',
          );
          setBusy(false);
          return;
        }
        input.brandGrants = brandGrants;
      }
      await createInvitation(input);
      magToast.success('sent.', `${email} — link goes to their inbox.`);
      await onSent();
      onClose();
    } catch (err) {
      if (err instanceof InvitationApiError) {
        if (err.status === 402) {
          magToast.error('seat cap.', err.message);
        } else if (err.code === 'already_member') {
          magToast.warning('already here.', err.message);
        } else {
          magToast.error("couldn't send.", err.message);
        }
      } else {
        magToast.error("couldn't send.", founderErrorCopy(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const node = (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop intentionally non-interactive
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[var(--ink)]/35 px-4 py-8 sm:px-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-dialog-title"
        // No overflow-hidden: the brand-grant Select pops a dropdown
        // that needs to escape the modal bounds. We round the footer
        // explicitly so the corners still look clipped.
        className="w-full max-w-[480px] rounded-2xl border-[1.5px] border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--plum)]"
      >
        <form onSubmit={handleSubmit}>
          <div className="px-5 pt-5">
            <h2
              id="invite-dialog-title"
              className="font-display text-[20px] font-bold leading-tight tracking-[-0.01em]"
            >
              <Trans>invite teammate</Trans>
            </h2>
            <p className="mt-1 text-[13px] text-[var(--ink)]/70">
              <Trans>they'll get an email from mag with a 7-day link.</Trans>
            </p>

            <label className="mt-4 block">
              <span className="block font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--ink)]/60">
                <Trans>email</Trans>
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t`vincent@acme.co`}
                className="mt-1 w-full rounded-md border border-[var(--line)] bg-white px-3 py-2 font-mono text-[13px] focus:border-[var(--ink)] focus:outline-none"
              />
            </label>

            <fieldset className="mt-4">
              <legend className="font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--ink)]/60">
                <Trans>workspace role</Trans>
              </legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <RoleCard
                  label={t`admin`}
                  desc={t`every brand, every approval`}
                  active={workspaceRole === 'admin'}
                  onClick={() => setWorkspaceRole('admin')}
                />
                <RoleCard
                  label={t`member`}
                  desc={t`only the brands you grant below`}
                  active={workspaceRole === 'member'}
                  onClick={() => setWorkspaceRole('member')}
                />
              </div>
            </fieldset>

            {workspaceRole === 'member' ? (
              <fieldset className="mt-4">
                <legend className="font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--ink)]/60">
                  <Trans>brand grants</Trans>
                </legend>
                {brands.length === 0 ? (
                  <p className="mt-2 text-[13px] text-[var(--ink)]/55">
                    <Trans>no brands yet — create one before inviting a member.</Trans>
                  </p>
                ) : (
                  <ul className="mt-2 divide-y divide-[var(--line)] rounded-md border border-[var(--line)]">
                    {brands.map((b) => (
                      <li key={b.id} className="flex items-center justify-between gap-3 px-3 py-2">
                        <span className="truncate text-[13px]">{b.name.toLowerCase()}</span>
                        <div className="w-[164px] shrink-0">
                          <Select<'' | 'manager' | 'editor' | 'viewer' | 'designer'>
                            value={grants[b.id] ?? ''}
                            onChange={(role) => setGrants((prev) => ({ ...prev, [b.id]: role }))}
                            options={BRAND_GRANT_OPTIONS}
                            ariaLabel={`access for ${b.name}`}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </fieldset>
            ) : null}
          </div>

          <div className="mt-5 flex items-center justify-end gap-3 rounded-b-2xl border-t border-[var(--line)] bg-[var(--paper)] px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="link-quiet text-[12px] disabled:opacity-60"
            >
              <Trans>cancel</Trans>
            </button>
            <button type="submit" disabled={busy} className="btn btn-spell disabled:opacity-50">
              {busy ? <Trans>sending…</Trans> : <Trans>send invite</Trans>}
              {!busy ? <span className="btn-arrow">→</span> : null}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

function RoleCard({
  label,
  desc,
  active,
  onClick,
}: {
  label: string;
  desc: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-md border-[1.5px] border-[var(--ink)] bg-[var(--spell)] px-3 py-2 text-left shadow-[2px_2px_0_var(--ink)]'
          : 'rounded-md border border-[var(--line)] bg-white px-3 py-2 text-left hover:border-[var(--ink)]/40'
      }
    >
      <p className="font-mono text-[12px] font-semibold uppercase tracking-[0.08em]">{label}</p>
      <p className="mt-0.5 text-[11.5px] text-[var(--ink)]/65">{desc}</p>
    </button>
  );
}
